import { Injectable, Signal, computed } from '@angular/core';
import { patchState, signalStore, withState } from '@ngrx/signals';

import { FacetSelectionService } from './facet-selection-service';

const EXPANDED_SELECTION = 'expanded';
const EXPANDED_SELECTION_MARKER = 'expansion';

const SEARCH_EXPANDED_SELECTION = 'searchExpanded';
const SEARCH_EXPANDED_SELECTION_MARKER = 'search-expansion';

const FACET_EXPANDED_DEFAULT_VALUE = true;
const FACET_SEARCH_EXPANDED_DEFAULT_VALUE = false;

interface FacetExpansionServiceState {
  selectionKey: string | undefined;
}

export const INITIAL_STATE: FacetExpansionServiceState = {
  selectionKey: undefined,
};

const FacetExpansionServiceStoreBase = signalStore({ protectedState: false }, withState(INITIAL_STATE));

@Injectable()
export class FacetExpansionStateService extends FacetExpansionServiceStoreBase {
  private readonly expansionState: Signal<FacetSelectionService.SelectionState | undefined> = computed(() =>
    this.getExpansionState(EXPANDED_SELECTION_MARKER),
  );

  private readonly searchExpansionState: Signal<FacetSelectionService.SelectionState | undefined> = computed(() =>
    this.getExpansionState(SEARCH_EXPANDED_SELECTION_MARKER),
  );

  constructor(private readonly selectionService: FacetSelectionService) {
    super();
  }

  setSelectionKey(selectionKey: string | undefined): void {
    patchState(this, { selectionKey });
  }

  isExpansionSet(stateKey: string = EXPANDED_SELECTION): boolean {
    const expansionState: FacetSelectionService.SelectionState | undefined = this.expansionState();
    return !!expansionState?.isSet(stateKey);
  }

  isExpanded(stateKey: string = EXPANDED_SELECTION, fallback = FACET_EXPANDED_DEFAULT_VALUE): boolean {
    const expansionState: FacetSelectionService.SelectionState | undefined = this.expansionState();
    return this.resolveExpansionStateValue(expansionState, stateKey, fallback);
  }

  isSearchExpanded(stateKey: string = SEARCH_EXPANDED_SELECTION): boolean {
    const expansionState: FacetSelectionService.SelectionState | undefined = this.searchExpansionState();
    return this.resolveExpansionStateValue(expansionState, stateKey, FACET_SEARCH_EXPANDED_DEFAULT_VALUE);
  }

  setExpanded(expanded: boolean, stateKey: string = EXPANDED_SELECTION): void {
    this.expansionState()?.toggleSelected(stateKey, expanded);
  }

  setSearchExpanded(expanded: boolean, stateKey: string = SEARCH_EXPANDED_SELECTION): void {
    this.searchExpansionState()?.toggleSelected(stateKey, expanded);
  }

  private resolveExpansionStateValue(
    expansionState: FacetSelectionService.SelectionState | undefined,
    stateKey: string,
    fallback: boolean,
  ): boolean {
    if (!expansionState || !expansionState.isSet(stateKey)) {
      return fallback;
    }

    return expansionState.isSelected(stateKey);
  }

  private getExpansionState(stateMarker: string): FacetSelectionService.SelectionState | undefined {
    const selectionKey: string | undefined = this.selectionKey();

    if (!selectionKey) {
      return undefined;
    }

    return this.selectionService.getSelectionState(`${selectionKey}_$$${stateMarker}`);
  }
}
